import React, { useState, useEffect } from "react";
import {
  Table,
  Space,
  DatePicker,
  Card,
  Button,
  Statistic,
  Input,
  message,
} from "antd";
import { Link } from "react-router-dom";
import Layouts from "../../components/Layouts";
import { getDirectusClient } from "../../lib/directus";
import { readItems, readUsers } from "@directus/sdk";
import { useSelector } from "react-redux";
import validarpermiso from "../../lib/utils";
import moment from "moment";
import VendedorFacturasModal from "./components/VendedorFacturasModal";
const { RangePicker } = DatePicker;
const { Search } = Input;

export default function ReporteVendedores() {
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [dateRange, setDateRange] = useState([moment(), moment()]);
  const permisos = useSelector((state) => state.permisos);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("vendedor"); // 'vendedor' or 'supervisor'

  const calculateProductTotal = (producto) => {
    const subtotal = producto.cantidad * producto.precio_unitario;
    const impuesto = producto.producto?.grupo?.impuesto || 0;
    const impuestoValue = subtotal * (impuesto / 100);
    return subtotal + impuestoValue;
  };

  const calculateFacturaTotal = (factura) => {
    return factura.productos.reduce((acc, producto) => {
      return acc + calculateProductTotal(producto);
    }, 0);
  };

  const getFacturasFilter = (vendedorId) => ({
    _and: [
      { vendedor_factura: { _eq: vendedorId } },
      { estado: { _neq: "devolucion" } },
      { estado: { _neq: "devolcion de factura" } },
      {
        date_created: {
          _between: [
            dateRange[0].startOf('day').toISOString(),
            dateRange[1].endOf('day').toISOString()
          ]
        }
      }
    ],
  });

  const fetchVendedores = async () => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ["*", "role.*", "superior.*"],
          filter: { role: { name: { _contains: "vendedor" } } },
        })
      );

      const vendedoresWithSales = await Promise.all(
        response.map(async (vendedor) => {
          const facturas = await directus.request(
            readItems("facturas", {
              fields: [
                "id",
                "consecutivo",
                "date_created",
                "estado",
                "tipo",
                "cliente.*",
                "vendedor_factura.*",
                "vendedor_prestamo.*",
                "productos.*",
                "productos.producto.*",
                "productos.producto.grupo.impuesto",
                "user_created.first_name",
                "user_created.last_name",
                "productos.seriales.*",
                "productos.seriales.estado.estado",
                "bodega.*",
                "caja.*",
          
                "caja.estado.estado",],
              filter: getFacturasFilter(vendedor.id),
            })
          );

          const totalVentas = facturas.reduce((sum, factura) => {
            return sum + calculateFacturaTotal(factura);
          }, 0);

          return {
            ...vendedor,
            facturas,
            totalVentas,
          };
        })
      );

      setVendedores(vendedoresWithSales);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendedores();
  }, [selectedDate, dateRange]);

  const getFilteredVendedores = () => {
    if (!searchText) return vendedores;

    return vendedores.filter((vendedor) => {
      const fullName =
        `${vendedor.first_name} ${vendedor.last_name}`.toLowerCase();
      const supervisorName =
        `${vendedor.superior?.first_name} ${vendedor.superior?.last_name}`.toLowerCase();
      const searchLower = searchText.toLowerCase();

      if (searchType === "vendedor") {
        return fullName.includes(searchLower);
      } else {
        return supervisorName.includes(searchLower);
      }
    });
  };

  const columns = [
    {
      title: "Vendedor",
      key: "vendedor",
      render: (_, record) => (
        <Space direction="vertical">
          <span>
            {record.first_name} {record.last_name}
          </span>
          <small>
            Supervisor: {record.superior?.first_name}{" "}
            {record.superior?.last_name}
          </small>
        </Space>
      ),
    },
    {
      title: "Total Facturas",
      key: "totalFacturas",
      render: (_, record) => record.facturas?.length || 0,
    },
    {
      title: "Total Ventas",
      dataIndex: "totalVentas",
      render: (value) => `$${value.toLocaleString()}`,
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedVendedor(record);
            setModalVisible(true);
          }}
        >
          Ver Facturas
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      {validarpermiso("facturas", "read", permisos) ? (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Card>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Space>
                <DatePicker
           
                  onChange={(date) => {
                    setSelectedDate(date || moment());
                    setDateRange([date || moment(), date || moment()]);
                    fetchVendedores();
                  }}
                  format="YYYY-MM-DD"
                  allowClear={false}
                />
                <RangePicker
             
                  onChange={(dates) => {
                    if (dates) {
                      setDateRange(dates);
                      setSelectedDate(dates[0]);
                      fetchVendedores();
                    } else {
                      const today = moment();
                      setDateRange([today, today]);
                      setSelectedDate(today);
                      fetchVendedores();
                    }
                  }}
                  format="YYYY-MM-DD"
                  allowClear={false}
                />
                <Statistic
                  title="Total Ventas del Período"
                  value={vendedores.reduce(
                    (sum, vendedor) => sum + vendedor.totalVentas,
                    0
                  )}
                  prefix="$"
                />
              </Space>
              <Space>
                <Search
                  placeholder={`Buscar por ${searchType}`}
                  allowClear
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                />
                <Button
                  type={searchType === "vendedor" ? "primary" : "default"}
                  onClick={() => setSearchType("vendedor")}
                >
                  Buscar por Vendedor
                </Button>
                <Button
                  type={searchType === "supervisor" ? "primary" : "default"}
                  onClick={() => setSearchType("supervisor")}
                >
                  Buscar por Supervisor
                </Button>
                <Link to="/list-factura">
                  <Button>Ver lista de facturas</Button>
                </Link>
              </Space>
            </Space>
          </Card>

          <Table
            dataSource={getFilteredVendedores()}
            columns={columns}
            loading={loading}
            rowKey="id"
          />

          <VendedorFacturasModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            vendedor={selectedVendedor}
            fecha={selectedDate}
          />
        </Space>
      ) : (
        <h3>No tiene permisos para ver este reporte</h3>
      )}
    </Layouts>
  );
}
